import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MapPin, Plus, X, Pencil, Check, Image as ImageIcon, LocateFixed, Rotate3D } from 'lucide-react';
import { POI_CATEGORIES, type PoiCategory } from '@/pages/Map';
import { type VirtualTour } from '@/components/VirtualTourManager';

interface Poi {
  id: string;
  name: string;
  description: string | null;
  latitude: string;
  longitude: string;
  category: string;
  color: string;
  icon: string;
  imageUrl: string | null;
  virtualTourId: string | null;
  displayOrder: number;
}

interface PoiFormState {
  name: string;
  description: string;
  latitude: string;
  longitude: string;
  category: PoiCategory;
  color: string;
  icon: string;
  imageUrl: string | null;
  imageFile: File | null;
  virtualTourId: string | null;
}

const BLANK_FORM: PoiFormState = {
  name: '',
  description: '',
  latitude: '',
  longitude: '',
  category: 'general',
  color: '#3B82F6',
  icon: '📍',
  imageUrl: null,
  imageFile: null,
  virtualTourId: null,
};

const CATEGORY_COLORS: Record<string, string> = {
  general:     '#3B82F6',
  naturaleza:  '#22C55E',
  cultura:     '#A855F7',
  gastronomia: '#F97316',
  servicios:   '#EAB308',
  deporte:     '#06B6D4',
  alojamiento: '#EC4899',
  transporte:  '#6B7280',
};

interface PoiManagerProps {
  municipalityId: string;
  defaultLatitude?: number;
  defaultLongitude?: number;
}

export const PoiManager = ({ municipalityId, defaultLatitude, defaultLongitude }: PoiManagerProps) => {
  const { toast } = useToast();
  const [pois, setPois] = useState<Poi[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PoiFormState>(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [virtualTours, setVirtualTours] = useState<VirtualTour[]>([]);

  const loadPois = useCallback(async () => {
    try {
      const data = await api.pois.list(municipalityId);
      setPois((data ?? []).map((p: Record<string, unknown>) => ({
        ...p,
        id: String(p.id),
        name: String(p.name ?? ''),
        description: p.description ? String(p.description) : null,
        latitude: String(p.latitude ?? ''),
        longitude: String(p.longitude ?? ''),
        category: String(p.category ?? 'general'),
        color: String(p.color ?? '#3B82F6'),
        icon: String(p.icon ?? '📍'),
        imageUrl: p.imageUrl || p.image_url ? String(p.imageUrl ?? p.image_url) : null,
        virtualTourId: p.virtualTourId || p.virtual_tour_id ? String(p.virtualTourId ?? p.virtual_tour_id) : null,
        displayOrder: Number(p.displayOrder ?? p.display_order ?? 0),
      })));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [municipalityId]);

  useEffect(() => {
    void loadPois();
    void api.virtualTours.listAdmin(municipalityId)
      .then((data) => setVirtualTours((data ?? []).map((tour: Record<string, unknown>) => ({
        id: String(tour.id),
        title: String(tour.title ?? ''),
        description: tour.description ? String(tour.description) : null,
        panoramaUrl: String(tour.panoramaUrl ?? tour.panorama_url ?? ''),
        thumbnailUrl: tour.thumbnailUrl || tour.thumbnail_url ? String(tour.thumbnailUrl ?? tour.thumbnail_url) : null,
        displayOrder: Number(tour.displayOrder ?? tour.display_order ?? 0),
        isPublished: Boolean(tour.isPublished ?? tour.is_published ?? true),
      }))))
      .catch((error) => console.error('Error loading virtual tours:', error));
  }, [loadPois, municipalityId]);

  const openCreate = () => {
    setForm(BLANK_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (poi: Poi) => {
    setForm({
      name: poi.name,
      description: poi.description ?? '',
      latitude: poi.latitude,
      longitude: poi.longitude,
      category: poi.category as PoiCategory,
      color: poi.color,
      icon: poi.icon,
      imageUrl: poi.imageUrl,
      imageFile: null,
      virtualTourId: poi.virtualTourId,
    });
    setEditingId(poi.id);
    setShowForm(true);
  };

  const handleCategoryChange = (val: string) => {
    setForm(prev => ({
      ...prev,
      category: val as PoiCategory,
      color: CATEGORY_COLORS[val] ?? prev.color,
      icon: POI_CATEGORIES.find(c => c.value === val)?.emoji ?? prev.icon,
    }));
  };

  const handleSave = async () => {
    if (!form.name || !form.latitude || !form.longitude) {
      toast({ title: 'Error', description: 'Nombre, latitud y longitud son obligatorios', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      // Upload image if a new file was selected
      let imageUrl = form.imageUrl;
      if (form.imageFile) {
        const ext = form.imageFile.name.split('.').pop();
        const slug = form.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
        const path = `pois/${municipalityId}-${slug}.${ext}`;
        const { url } = await api.storage.upload(form.imageFile, path, 'images');
        imageUrl = url;
      }

      const payload = {
        name: form.name,
        description: form.description || null,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        category: form.category,
        color: form.color,
        icon: form.icon,
        image_url: imageUrl,
        virtual_tour_id: form.virtualTourId,
        display_order: editingId ? pois.find(p => p.id === editingId)?.displayOrder ?? 0 : pois.length,
      };
      if (editingId) {
        await api.pois.update(municipalityId, editingId, payload);
        toast({ title: 'POI actualizado' });
      } else {
        await api.pois.create(municipalityId, payload);
        toast({ title: 'POI creado' });
      }
      setShowForm(false);
      setEditingId(null);
      await loadPois();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo guardar el punto';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.pois.delete(municipalityId, id);
      toast({ title: 'POI eliminado' });
      await loadPois();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo eliminar el punto';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-5 w-5 text-primary" />
              Puntos del mapa
              <Badge variant="secondary">{pois.length}</Badge>
            </CardTitle>
            <CardDescription>
              Crea marcadores con imagen, categoría y coordenadas. También puedes enlazarlos con una visita 360.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openCreate}
            className="w-full sm:w-auto hover:bg-primary hover:text-primary-foreground"
          >
            <Plus className="h-4 w-4 mr-2" />
            Añadir POI
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Inline form */}
        {showForm && (
          <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
            <p className="text-sm font-semibold">{editingId ? 'Editar punto' : 'Nuevo punto'}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Nombre *</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Iglesia de San Pedro"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Categoría</Label>
                <Select value={form.category} onValueChange={handleCategoryChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {POI_CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Latitud *</Label>
                <Input
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={e => setForm(prev => ({ ...prev, latitude: e.target.value }))}
                  placeholder="40.9083"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Longitud *</Label>
                <Input
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={e => setForm(prev => ({ ...prev, longitude: e.target.value }))}
                  placeholder="-3.5167"
                />
              </div>
              <div className="md:col-span-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!Number.isFinite(defaultLatitude) || !Number.isFinite(defaultLongitude)}
                  onClick={() => setForm((current) => ({
                    ...current,
                    latitude: String(defaultLatitude ?? ''),
                    longitude: String(defaultLongitude ?? ''),
                  }))}
                >
                  <LocateFixed className="mr-2 h-4 w-4" />
                  Usar centro del municipio
                </Button>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Icono (emoji)</Label>
                <Input
                  value={form.icon}
                  onChange={e => setForm(prev => ({ ...prev, icon: e.target.value }))}
                  placeholder="📍"
                  maxLength={4}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Color del marcador</Label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={form.color}
                    onChange={e => setForm(prev => ({ ...prev, color: e.target.value }))}
                    className="h-10 w-14 rounded cursor-pointer border"
                  />
                  <span className="text-sm text-muted-foreground">{form.color}</span>
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Visita 360 vinculada</Label>
              <Select
                value={form.virtualTourId ?? 'none'}
                onValueChange={(value) => setForm((current) => ({
                  ...current,
                  virtualTourId: value === 'none' ? null : value,
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin visita 360" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin visita 360</SelectItem>
                  {virtualTours.map((tour) => (
                    <SelectItem key={tour.id} value={tour.id}>{tour.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Al tocar el marcador, el visitante podrá abrir directamente este panorama.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Descripción</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción breve del lugar..."
                rows={2}
              />
            </div>
            {/* Image upload */}
            <div className="space-y-1">
              <Label className="text-xs">Imagen del lugar</Label>
              {(form.imageUrl || form.imageFile) ? (
                <div className="flex items-center gap-3">
                  <img
                    src={form.imageFile ? URL.createObjectURL(form.imageFile) : form.imageUrl!}
                    alt="preview"
                    className="h-20 w-32 object-cover rounded-lg border"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setForm(prev => ({ ...prev, imageUrl: null, imageFile: null }))}
                    className="text-destructive"
                  >
                    <X className="h-4 w-4 mr-1" /> Quitar
                  </Button>
                </div>
              ) : (
                <label className="flex items-center gap-2 px-3 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors">
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Seleccionar imagen...</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) setForm(prev => ({ ...prev, imageFile: file, imageUrl: null }));
                    }}
                  />
                </label>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditingId(null); }}>
                <X className="h-4 w-4 mr-1" /> Cancelar
              </Button>
              <Button type="button" size="sm" onClick={handleSave} disabled={saving}>
                <Check className="h-4 w-4 mr-1" /> {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>
        )}

        {/* POI list */}
        {loading && <p className="text-sm text-muted-foreground text-center py-4">Cargando...</p>}
        {!loading && pois.length === 0 && !showForm && (
          <p className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded-lg">
            No hay puntos de interés. Añade el primero con el botón de arriba.
          </p>
        )}
        {pois.map(poi => (
          <div key={poi.id} className="flex items-start gap-3 p-3 border rounded-lg bg-background">
            <span
              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-lg mt-0.5"
              style={{ backgroundColor: poi.color + '22', border: `2px solid ${poi.color}` }}
            >
              {poi.icon}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{poi.name}</p>
              <p className="text-xs text-muted-foreground">
                {POI_CATEGORIES.find(c => c.value === poi.category)?.label ?? poi.category}
                {' · '}{parseFloat(poi.latitude).toFixed(5)}, {parseFloat(poi.longitude).toFixed(5)}
              </p>
              {poi.description && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{poi.description}</p>
              )}
              {poi.virtualTourId && (
                <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary">
                  <Rotate3D className="h-3.5 w-3.5" /> Visita 360 vinculada
                </span>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => openEdit(poi)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(poi.id)}
                className="text-destructive hover:text-destructive"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
